import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
} from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { possibleTypes } from "@package/ui-gql/index-data-access";
import { persistCache, type PersistentStorage } from "apollo3-cache-persist";
import { TimeoutLink } from "../links/link-timeout";

const TIMEOUT_MS = 60 * 1000;
const TIMEOUT_RETRY_STEP_EXP_MS = 1000;
const TIMEOUT_RETRY_MAX_MS = 5 * 60 * 1000;
const TIMEOUT_RETRY_MAX_ATTEMPTS = 36;

type ApolloClientProps = {
  environment?: string;
  name?: string;
  organization?: string;
  linkDetailedTimeoutRetry?: boolean;
  linkRetryIndefinitely?: boolean;
  storage?: PersistentStorage<string>;
  token: string;
  uri: string;
};

let requestId = 0;

export async function createApolloClient({
  environment = "local",
  name = "panoscape-cms",
  organization,
  linkDetailedTimeoutRetry = false,
  linkRetryIndefinitely = false,
  storage,
  token,
  uri,
}: ApolloClientProps) {
  const cache = new InMemoryCache({
    possibleTypes: possibleTypes.possibleTypes,
  });
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  const linkHttp = new HttpLink({ headers, uri });
  const linkError = new ErrorLink(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message }) =>
        console.error(`[apollo-client]: GraphQL Error: ${message}`),
      );
    }
    if (networkError) {
      console.error(`[apollo-client]: Network Error: ${networkError.message}`);
    }
  });
  const linkLogger = new ApolloLink((operation, forward) => {
    const id = ++requestId;
    console.log(
      `[apollo-client]: GraphQL Request ${id}: ${operation.operationName}`,
    );
    operation.setContext({
      start: Date.now(),
    });

    return forward(operation).map((response) => {
      const responseTime = Date.now() - operation.getContext().start;
      console.log(
        `[apollo-client]: GraphQL Request ${id} Response ${operation.operationName} took: ${responseTime}`,
      );
      return response;
    });
  });
  const linkTimeout = new TimeoutLink(TIMEOUT_MS);
  const linkRetry = new RetryLink({
    delay: {
      initial: TIMEOUT_RETRY_STEP_EXP_MS,
      max: TIMEOUT_RETRY_MAX_MS,
      jitter: true,
    },
    attempts: {
      max: linkRetryIndefinitely ? TIMEOUT_RETRY_MAX_ATTEMPTS : 5,
      retryIf: (error, operation) => {
        if (error) {
          if (
            operation.query.definitions.some(
              (d) =>
                d.kind === "OperationDefinition" && d.operation === "query",
            )
          ) {
            console.log(
              `[apollo-client]: Retrying request ${operation.operationName} due to error:`,
              error,
            );
            return true;
          }
        }

        return false;
      },
    },
  });
  const link = linkDetailedTimeoutRetry
    ? from([linkRetry, linkLogger, linkTimeout, linkError, linkHttp])
    : undefined;
  let apolloCacheKey = `apollo-cache-${name}-${environment}`;

  if (organization) {
    headers["x-org-id"] = organization;
    apolloCacheKey = `${apolloCacheKey}-${organization}`;
  }

  if (storage) {
    await persistCache({
      cache,
      key: apolloCacheKey,
      storage,
    });
  }

  return new ApolloClient({
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: storage ? "cache-first" : "no-cache",
        errorPolicy: "ignore",
      },
      query: {
        fetchPolicy: storage ? "cache-first" : "no-cache",
        errorPolicy: "all",
      },
    },
    headers,
    link,
    name,
    queryDeduplication: true,
    uri,
    version: "1.3",
  });
}
